import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { localizationNl } from '../Localizations/nl'
import { localizationEn } from '../Localizations/en'
import { localizationFr } from '../Localizations/fr'

export const adminViews = [
  'certificate-instance-add',
  'certificate-instance-edit',
  'user-list',
  'user-detail',
  'user-add',
  'certificate-type-list',
  'certificate-type-add',
  'certificate-type-edit',
  'logging-list',
  'reporting',
] as const
export const secureViews = [
  'certificate-add',
  'certificate-edit',
  'company-add',
  'company-edit',
  ...adminViews,
] as const

export const availableViews = [
  'login',
  'certificate-list',
  'certificate-detail',
  'company-list',
  'company-detail',
  'certificate-instance-list',
  'certificate-instance-detail',
  ...secureViews,
] as const
export type AvailableView = (typeof availableViews)[number]
export declare type AvailableDestinationViews = '' | AvailableView

export const languages = ['nl', 'fr', 'en']
export declare type Language = (typeof languages)[number]
export type Popup =
  | { type: 'company-add'; state?: unknown }
  | {
      type: 'company-change-vat'
      state: { vatNumber: string }
    }

const initialState = {
  activeView: 'certificate-list' as AvailableView,
  destinationView: '' as AvailableDestinationViews,
  detailKey: undefined as string | undefined,
  flashMessage: undefined as FlashMessage | undefined,
  activeLanguage: 'nl' as Language,
  localization: localizationNl,
  activePopup: undefined as Popup | undefined,
  previousPopups: [] as Popup[],
}
const interfaceSlice = createSlice({
  name: 'interface',
  initialState,
  reducers: {
    setActiveView(state, action: PayloadAction<AvailableView>) {
      state.activeView = action.payload
    },
    setDestinationView(
      state,
      action: PayloadAction<{
        destinationView: AvailableDestinationViews
        detailKey?: string
      }>
    ) {
      state.destinationView = action.payload.destinationView
      state.detailKey = action.payload.detailKey
    },
    showFlashMessage(state, action: PayloadAction<FlashMessage>) {
      state.flashMessage = action.payload
    },
    clearFlashMessage(state) {
      state.flashMessage = undefined
    },
    setActiveLanguage(state, action: PayloadAction<Language>) {
      state.activeLanguage = action.payload
      switch (action.payload) {
        case 'fr':
          state.localization = localizationFr
          break
        case 'en':
          state.localization = localizationEn
          break
        default:
          // nl
          state.localization = localizationNl
          break
      }
    },
    setMissingLocalization(
      state,
      action: PayloadAction<{
        section: string
        key: string
        text: string
        parameters?: string[]
      }>
    ) {
      const { section, key, text, parameters } = action.payload
      if (state.localization[section] === undefined)
        state.localization[section] = {}
      state.localization[section][key] = { text, parameters }
    },
    showPopup(state, action: PayloadAction<Popup>) {
      if (state.activePopup) state.previousPopups.push(state.activePopup)
      state.activePopup = action.payload
    },
    closeCurrentPopup(state) {
      const previousPopup = state.previousPopups.pop()
      state.activePopup = previousPopup
    },
  },
})
export const {
  setActiveView,
  setDestinationView,
  showFlashMessage,
  clearFlashMessage,
  setActiveLanguage,
  setMissingLocalization,
  showPopup,
  closeCurrentPopup,
} = interfaceSlice.actions

export default interfaceSlice.reducer
interface FlashMessage {
  message: string
  icon?: 'error' | 'warning' | 'info' | 'success' | string
  type?: 'success' | 'error'
}
